import React, { useCallback } from "react";
import "./checkpoint-card.component.scoped.scss";
import { Box, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { history } from "react-router-guard";
import { educationRoutes } from "../../types/education.routes";
import ActionMenuComponent from "../../../reusable-components/components/menu.component";
import { icons } from "../../../../statics/icons";
import { formatDate } from "../../../../helpers";
import { useTranslation } from "react-i18next";
import { CheckpointStatus } from "../../types/checkpoint.types";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

const CheckpointCardComponent = ({
  checkpoint,
  loading,
  hasFullAccess = false,
  sectionLocked = false,
  onEdit,
  onDelete,
}: {
  checkpoint: any;
  loading: boolean;
  hasFullAccess?: boolean;
  sectionLocked?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) => {
  const { t } = useTranslation();

  const canOpenCheckpoint = useCallback(
    (checkpoint: any): boolean => {
      if (!checkpoint) return false;
      if (hasFullAccess) return true;
      return (
        checkpoint.status === "ACTIVE" &&
        checkpoint.questionsCount > 0 &&
        !checkpoint.locked &&
        !sectionLocked
      );
    },
    [hasFullAccess, sectionLocked],
  );

  const handleCheckpointClick = useCallback(
    (checkpoint: any) => {
      if (!checkpoint?.id || !canOpenCheckpoint(checkpoint)) return;
      history.push(
        hasFullAccess
          ? educationRoutes.checkpoint(checkpoint.id)
          : educationRoutes.exam(checkpoint.id),
      );
    },
    [canOpenCheckpoint, hasFullAccess],
  );

  return (
    <Box className="checkpoint__card p-3">
      {loading ? (
        <Skeleton
          variant="text"
          height={20}
          width="80%"
          style={{ marginBottom: "10px" }}
        />
      ) : (
        <Box
          className=" align-items-center justify-content-between mb-2"
          style={{
            display: "flex",
            minHeight: "30px",
            gap: "0.5rem",
            flexWrap: "nowrap",
          }}
        >
          <Typography
            variant="h3"
            className="font-weight-bold d-flex"
            style={{
              gap: "0.5rem",
              cursor: canOpenCheckpoint(checkpoint) ? "pointer" : "default",
            }}
            onClick={() => {
              handleCheckpointClick(checkpoint);
            }}
          >
            {!hasFullAccess && (checkpoint.locked || sectionLocked) && (
              <img
                src={icons.LOCK_BLACK}
                alt="checkpoint"
                width={20}
                height={20}
              />
            )}
            {checkpoint.title}
          </Typography>

          {hasFullAccess && (
            <Box className="actions">
              <ActionMenuComponent
                key={checkpoint.id}
                items={[
                  {
                    label: t("EDIT"),
                    onClick: () => {
                      onEdit?.();
                    },
                  },
                  {
                    label: t("DELETE"),
                    onClick: () => {
                      onDelete?.();
                    },
                  },
                ]}
              />
            </Box>
          )}

          {!hasFullAccess ? (
            checkpoint.status === CheckpointStatus.INACTIVE ||
            checkpoint.questionsCount === 0 ? (
              <span className="icon-wrapper user-view">
                <img src={icons.LOCK} alt="lock" />
              </span>
            ) : checkpoint.result ? (
              <span
                className="result__label"
                style={{
                  backgroundColor:
                    checkpoint.result.result === CheckpointStatus.FAILED
                      ? "var(--danger)"
                      : "var(--success)",
                }}
              >
                {checkpoint.result === CheckpointStatus.FAILED
                  ? t("NOT_PASSED")
                  : t("ACHIEVED")}
              </span>
            ) : null
          ) : null}
        </Box>
      )}

      <Box className="d-flex align-items-center justify-content-between mb-4">
        {loading ? (
          <Skeleton variant="text" height={20} width="60%" />
        ) : (
          <Typography
            variant="caption"
            color="textSecondary"
            className="d-flex align-items-center"
            style={{ gap: "0.5rem" }}
          >
            <img src={icons.CALENDER} alt="calender" />{" "}
            {formatDate(checkpoint.createdAt, "ddd, MMM DD, HH:mm Z")}
          </Typography>
        )}
      </Box>
      <Box className="status__bar">
        <span className="progress__bar"></span>
        {loading ? (
          <Skeleton variant="text" height={20} width="100px" />
        ) : (
          <Box
            className="d-flex align-items-center justify-content-between"
            style={{ position: "relative" }}
          >
            <Typography variant="caption" color="textSecondary">
              {checkpoint.questionsCount} {t("EDUCATION_PAGE.QUESTIONS")}
            </Typography>
            {hasFullAccess && (
              <>
                {checkpoint.visibility === CheckpointStatus.ADMIN_ONLY ? (
                  <span className="icon-wrapper admin-view">
                    <VisibilityOff />
                  </span>
                ) : checkpoint.status === CheckpointStatus.INACTIVE ||
                  checkpoint.questionsCount === 0 ? (
                  <span className="icon-wrapper admin-view">
                    <img src={icons.LOCK} alt="lock" />
                  </span>
                ) : null}
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CheckpointCardComponent;
